<template>
	<div id="time-view">
		<div class="row">
			<div class="col-12">
				<h2>Ort</h2>
			</div>
			<div class="col-md-6">
				<b-field label="Abholung">
					<b-select 
						v-model="locations.pickUpBranch" 
						@input="onPickUpBranchInput" 
						expanded>
						<option 
							v-for="branch in branches" 
							:key="branch.id" 
							:value="branch">{{ branch.name }}</option>
					</b-select>
				</b-field>
				<business-hours 
					v-if="locations.pickUpBranch" 
					:business-hours="locations.pickUpBranch.BusinessHours" />
			</div>
			<div class="col-md-6">
				<b-field label="Rückgabe">
					<b-select 
						v-model="locations.returnBranch" 
						@input="onReturnBranchInput" 
						expanded>
						<option 
							v-for="branch in returnBranches" 
							:key="branch.id" 
							:value="branch">{{ branch.name }}</option>
					</b-select>
				</b-field>
				<business-hours 
					v-if="locations.returnBranch" 
					:business-hours="locations.returnBranch.BusinessHours" />
			</div>
		</div>
		<div 
			class="row" 
			v-if="locations.pickUpBranch">
			<div class="col-12">
				<h2>Mietzeitraum</h2>
			</div>
			<div class="col-md-4">
				<b-field 
					label="Abholdatum" 
					:type="pickUpBranchTimeValid ? 'is-danger' : null" 
					:message="returnBranchTimeValid ? 'Die ausgewählte Zeit liegt nicht in der Öffnungszeit der Filiale.' : ''">
					<b-datepicker
						@input="saveTimes"
						:min-date="firstPossibleStartDate"
						v-model="times.startDate" 
						:date-formatter="dateFormatter"
						:month-names="monthNames"/>
				</b-field>
			</div>
			<div class="col-md-2">
				<b-field 
					label="Abholzeit" 
					:type="pickUpBranchTimeValid ? 'is-danger' : null">
					<b-timepicker 
						@input="saveTimes" 
						v-model="times.startDate" 
						:increment-minutes="10"/>
				</b-field>
			</div>
			<div class="col-md-4">
				<b-field 
					label="Rückgabedatum" 
					:type="returnBranchTimeValid ? 'is-danger' : null" 
					:message="returnBranchTimeValid ? 'Die ausgewählte Zeit liegt nicht in der Öffnungszeit der Filiale.' : ''">
					<b-datepicker
						@input="saveTimes"
						:min-date="times.startDate"
						v-model="times.endDate"
						:date-formatter="dateFormatter"
						:month-names="monthNames"/>
				</b-field>
			</div>
			<div class="col-md-2">
				<b-field 
					label="Rückgabezeit" 
					:type="returnBranchTimeValid ? 'is-danger' : null">
					<b-timepicker 
						@input="saveTimes" 
						v-model="times.endDate" 
						:increment-minutes="10" />
				</b-field>
			</div>
		</div>
		<br>
		<div class="text-right">
			<button 
				class="button is-primary" 
				@click="next" 
				:disabled="!canNext">Weiter</button>
		</div>
	</div>
</template>

<script>
import moment from 'moment'
import { mapState, mapGetters } from 'vuex'
import BusinessHours from '../components/BusinessHours.vue'
import InputTime from '../components/InputTime.vue'
import InputSelect from '../components/InputSelect.vue'

export default {
	name: 'TimeView',
	components: {
		BusinessHours,
		InputTime,
		InputSelect
	},
	computed: {
		...mapState('branch', {
			branches: 'branches'
		}),
		...mapGetters('order', ['validDates']),
		canNext() {
			return (
				this.locations.pickUpBranch !== null &&
				this.locations.returnBranch !== null &&
				this.validDates.startDateValid &&
				this.validDates.endDateValid
			)
		},
		firstPossibleStartDate() {
			return moment()
				.add(1, 'day')
				.toDate()
		},
		returnBranchTimeValid() {
			return this.locations.returnBranch && !this.validDates.endDateValid
		},
		pickUpBranchTimeValid() {
			return this.locations.pickUpBranch && !this.validDates.startDateValid
		},
		monthNames() {
			return moment.months()
		},
		returnBranches() {
			if (this.locations.pickUpBranch) {
				const pickUpBranch = this.locations.pickUpBranch
				const branches = this.branches.filter(branch => {
					// Filter out branches that are not in ReturnBranches and are not the pickupBranch
					return (
						pickUpBranch.ReturnBranches.some(b => b === branch.id) ||
						branch.id === pickUpBranch.id
					)
				})
				return branches
			}
			return []
		}
	},
	data() {
		const startDate = moment(this.$store.state.order.startDate)
		const endDate = moment(this.$store.state.order.endDate)
		const { pickUpBranch, returnBranch } = this.$store.state.order
		return {
			locations: {
				pickUpBranch,
				returnBranch
			},
			times: {
				startDate: startDate.toDate(),
				endDate: endDate.toDate()
			}
		}
	},
	mounted() {
		this.saveTimes()
	},
	methods: {
		getFirstValidDate(branch, day, week) {
			const hours = branch.BusinessHours
			let date

			for (const hour of hours) {
				const [openHour, openMinute] = hour.open.split(':')
				const open = parseInt(hour.open.replace(':', ''))
				const close = parseInt(hour.close.replace(':', ''))
				if (day % 7 === hour.day) {
					date = moment({
						hour: openHour,
						minute: openMinute
					})
						.weekday(day + 1)
						.toDate()
					break
				}
			}
			if (!date && day < 20) {
				// The is there to prevent infinite recursions
				return this.getFirstValidDate(branch, day + 1)
			}
			return date
		},
		dateFormatter(date) {
			return moment(date).format('DD.MM.YYYY')
		},
		onPickUpBranchInput() {
			if (!this.locations.returnBranch && this.locations.pickUpBranch) {
				this.locations.returnBranch = this.locations.pickUpBranch
			}
			this.$store.commit('order/SET_LOCATIONS', this.locations)
			this.times.startDate = this.getFirstValidDate(
				this.locations.pickUpBranch,
				moment().day()
			)
			this.times.endDate = moment(this.times.startDate).add(2,'hours').toDate()

			// Save new times to Store
			this.saveTimes()
		},
		onReturnBranchInput() {
			this.$store.commit('order/SET_LOCATIONS', this.locations)
			this.times.endDate = this.getFirstValidDate(
				this.locations.pickUpBranch,
				moment().day() + 2
			)
			this.saveTimes()
		},
		saveTimes() {
			this.$store.commit('order/SET_TIMES', this.times)
		},
		next() {
			this.$emit('next')
		}
	}
}
</script>
